import { memo, useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';
import * as zod from 'zod';
import { CompanyUsersSelect } from 'src/components/domain/users/components/company-users-select';
import {
  LggDropdownButton,
  LggDropdownButtonWithoutOverlay,
} from 'src/components/general/button/dropdown-button';
import { BottomDrawer } from 'src/components/general/drawer/bottom/bottom-drawer';
import {
  OnlyMeAvatar,
  StyledStackedCompanyUserList,
  UnassignedPrefixIcon,
} from 'src/components/general/inputs/assignee-input-components';
import { Radio } from 'src/components/general/inputs/radio';
import { FlexRow } from 'src/components/layout/flex-row';
import { conversationDropdownButtonStyles } from 'src/components/pages/conversations/components/filters/shared';
import { ButtonsBar } from 'src/components/pages/conversations/components/general/buttons-bar';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentUser } from 'src/hooks/use-current-user';
import { useVisible } from 'src/hooks/use-visible';
import { ConversationAssigneeType } from 'src/services/conversation.service';

const StyledBottomDrawer = styled(BottomDrawer)`
  .scrollbar {
    padding: 20px;
  }

  & .scrollbar label:not(:last-of-type) {
    margin-bottom: 28px !important;
  }
`;

const StyledSelect = styled(CompanyUsersSelect)`
  margin-left: 30px;
  margin-top: 4px;
  width: calc(100% - 30px);
` as typeof CompanyUsersSelect;

const FiltersOverlay = styled.div`
  padding: 20px 20px 15px;
  width: 296px;

  label:not(:last-of-type) {
    margin-bottom: 28px !important;
  }
`;

const OverlayTitle = styled.span`
  color: ${({ theme }) => theme.colors.carbon};
  display: block;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  line-height: 1;
  margin-bottom: 28px;
  text-align: left;
`;
const StyledButtonsBar = styled(ButtonsBar)`
  border-top: 1px solid ${({ theme }) => theme.colors.koala};

  ${up('md')} {
    border-top: unset;
    justify-content: flex-end;
    padding: 15px 0 0;

    > * {
      font-size: 12px;
      padding: 0 15px;
      width: unset;
    }

    > * + * {
      margin-left: 15px;
    }
  }
`;

const dropdownButtonStyles = css`
  max-width: 134px;
  width: max-content;

  [data-element='leading-icon'] {
    svg {
      height: 16px;
      width: 16px;
    }
  }
`;

const DesktopDropdownButton = styled(LggDropdownButton)<{ $isActive: boolean }>`
  ${conversationDropdownButtonStyles};
  ${dropdownButtonStyles};

  ${({ $isActive }) =>
    $isActive &&
    css`
      &,
      &:hover,
      &:focus {
        background: ${({ theme }) => theme.colors.secondaryTopaz10};
        border: 1px solid ${({ theme }) => theme.colors.secondaryTopaz40};
      }
    `};
`;

const MobileDropdownButton = styled(LggDropdownButtonWithoutOverlay)<{
  $isActive: boolean;
}>`
  ${conversationDropdownButtonStyles};
  ${dropdownButtonStyles};

  ${({ $isActive }) =>
    $isActive &&
    css`
      &,
      &:hover,
      &:focus {
        background: ${({ theme }) => theme.colors.secondaryTopaz10};
        border: 1px solid ${({ theme }) => theme.colors.secondaryTopaz40};
      }
    `};
`;

const DropdownContentContainer = styled(FlexRow)`
  align-items: center;
  gap: 5px;
`;

const filtersFormSchema = zod.object({
  assigneeType: zod.nativeEnum(ConversationAssigneeType).nullish(),
  assignees: zod.z.array(zod.z.number()).optional(),
});

type FiltersFormValues = zod.infer<typeof filtersFormSchema>;

type AssigneesFilterProps = {
  selectedAssignees: number[];
  selectedAssigneeType: ConversationAssigneeType | null;
  onApply: (filters: {
    assignees: number[];
    assigneeType: ConversationAssigneeType | null;
  }) => void;
};

export const AssigneesFilter = memo<AssigneesFilterProps>(
  ({ onApply, selectedAssigneeType, selectedAssignees }) => {
    const filterForm = useForm<FiltersFormValues>({
      resolver: zodResolver(filtersFormSchema),
      defaultValues: {
        assignees: selectedAssignees,
        assigneeType: selectedAssigneeType,
      },
    });
    const { hasPermission } = useAuthorization();
    const currentUser = useCurrentUser();
    const isManager = hasPermission('platform:company.contact:read:all');
    const { t } = useTranslation(['conversations', 'common']);
    const assigneesTypes: ReadonlyArray<{
      value: ConversationAssigneeType;
      testId: string;
      label: string;
    }> = [
      {
        value: ConversationAssigneeType.ONLY_ME,
        testId: 'assignee-radio-only_me',
        label: t('conversations:assigneesFilters.onlyMe'),
      },
      {
        value: ConversationAssigneeType.UNASSIGNED,
        testId: 'assignee-radio-unassigned',
        label: t('conversations:assigneesFilters.unassigned'),
      },
      {
        value: ConversationAssigneeType.CUSTOM,
        testId: 'assignee-radio-custom',
        label: t('conversations:assigneesFilters.custom'),
      },
    ].filter((assigneeType) => {
      if (!isManager) {
        return [
          ConversationAssigneeType.ONLY_ME,
          ConversationAssigneeType.UNASSIGNED,
        ].includes(assigneeType.value);
      }
      return true;
    });
    const visibilityHandler = useVisible();
    const breakpointUpMd = useBreakpoint(up('md'));
    const handleRadioChange = (value: ConversationAssigneeType) => {
      const setValueConfig = {
        shouldDirty: true,
      };
      filterForm.setValue('assigneeType', value, setValueConfig);
      filterForm.setValue('assignees', [], setValueConfig);
      filterForm.clearErrors();
    };
    const handleReset = () => {
      onApply({ assignees: [], assigneeType: null });
      visibilityHandler.close();
    };

    const handleClose = () => {
      filterForm.clearErrors();
      filterForm.reset({
        assignees: selectedAssignees,
        assigneeType: selectedAssigneeType,
      });

      visibilityHandler.close();
    };

    const handleCustomAssigneeChange = useCallback(
      (options) => {
        const validUserIds = options
          .map((option) => {
            const id = Number(option.value);

            return Number.isNaN(id) ? null : id;
          })
          .filter((id): id is number => id !== null);

        filterForm.setValue('assignees', validUserIds, { shouldDirty: true });
      },
      [filterForm],
    );

    const buttonsBar = (
      <Controller
        control={filterForm.control}
        name="assigneeType"
        rules={{
          required: true,
        }}
        render={({ field: assigneeTypeField = null }) => (
          <StyledButtonsBar
            primaryCallback={filterForm.handleSubmit(
              ({ assigneeType = null, assignees = [] }) => {
                if (
                  assigneeType === ConversationAssigneeType.CUSTOM &&
                  !assignees?.length
                ) {
                  filterForm.setError('assignees', { type: 'required' });
                  return;
                }

                onApply({ assignees, assigneeType });
                visibilityHandler.close();
              },
            )}
            secondaryCallback={handleReset}
            primaryText={t('common:filters.actionsApply')}
            secondaryText={t('common:filters.actionReset')}
            primaryButtonTestId="assignees-filters-apply"
            secondaryButtonTestId="assignees-filters-reset"
            disablePrimary={!assigneeTypeField?.value}
          />
        )}
      />
    );
    const formInputs = (
      <>
        <Controller
          control={filterForm.control}
          name="assigneeType"
          rules={{
            required: true,
          }}
          render={({ field: assigneeTypeField }) => (
            <>
              {assigneesTypes.map(({ value, label, testId }) => (
                <Radio
                  {...assigneeTypeField}
                  key={value}
                  value={value}
                  text={label}
                  testId={testId}
                  name="assignee-type"
                  onChange={() => {
                    handleRadioChange(value);
                  }}
                  checked={assigneeTypeField.value === value}
                />
              ))}
              {isManager && (
                <Controller
                  control={filterForm.control}
                  name="assignees"
                  render={({ field: assigneesField, fieldState }) => (
                    <StyledSelect
                      {...assigneesField}
                      name="assignees-filter-custom-select"
                      isDisabled={
                        assigneeTypeField.value !== ConversationAssigneeType.CUSTOM
                      }
                      isMulti
                      mobileLabel={t('conversations:assigneesFilters.title')}
                      placeholder={t(
                        'conversations:assigneesFilters.assigneesPlaceholder',
                      )}
                      selectedUsersIds={assigneesField.value ?? []}
                      onChange={handleCustomAssigneeChange}
                      hasError={Boolean(fieldState.error)}
                    />
                  )}
                />
              )}
            </>
          )}
        />
      </>
    );

    const prefixContent = useMemo(
      () =>
        match(selectedAssigneeType)
          .with(ConversationAssigneeType.ONLY_ME, () => (
            <OnlyMeAvatar user={currentUser} />
          ))
          .with(ConversationAssigneeType.UNASSIGNED, () => (
            <UnassignedPrefixIcon type="everyone" />
          ))
          .with(ConversationAssigneeType.CUSTOM, () => (
            <StyledStackedCompanyUserList
              selectedUsersIds={selectedAssignees}
              maxItems={2}
            />
          ))
          .otherwise(() => null),
      [currentUser, selectedAssigneeType, selectedAssignees],
    );

    const dropdownContent = useMemo(
      () => (
        <DropdownContentContainer>
          {prefixContent}
          {t('conversations:assigneesFilters.assignee')}
        </DropdownContentContainer>
      ),
      [prefixContent, t],
    );

    const hasValue = useMemo(() => selectedAssigneeType !== null, [selectedAssigneeType]);

    if (breakpointUpMd) {
      return (
        <>
          <DesktopDropdownButton
            size="small"
            $isActive={hasValue}
            icon={hasValue ? undefined : 'everyone'}
            data-lgg-id="assignees-filters-trigger-button"
            variant="defaultGhost"
            customDropdownProps={{
              placement: 'bottomRight',
              onVisibleChange: (visible) => {
                if (!visible) {
                  handleClose();
                }
              },
            }}
            overlay={
              <FiltersOverlay data-lgg-id="conversations-filters-overlay">
                <OverlayTitle>{t('conversations:assigneesFilters.title')}</OverlayTitle>
                {formInputs}
                {buttonsBar}
              </FiltersOverlay>
            }
            visibilityHandler={visibilityHandler}
          >
            {dropdownContent}
          </DesktopDropdownButton>
        </>
      );
    }

    return (
      <>
        <MobileDropdownButton
          variant="defaultWhite"
          size="regular"
          $isActive={hasValue}
          icon={hasValue ? undefined : 'everyone'}
          isActive={visibilityHandler.visible}
          onClick={() => visibilityHandler.setVisible(!visibilityHandler.visible)}
        >
          {dropdownContent}
        </MobileDropdownButton>
        {!breakpointUpMd && (
          <StyledBottomDrawer
            fullHeight
            title={t('conversations:assigneesFilters.title')}
            visible={visibilityHandler.visible}
            onClose={handleClose}
            footer={buttonsBar}
          >
            {formInputs}
          </StyledBottomDrawer>
        )}
      </>
    );
  },
);
